import { Component } from '@theme/component';

export class InternationalDialogHandler extends Component {
  // Don't require dialogComponent initially since it will be injected dynamically
  /** @type {string[]} */
  requiredRefs = [];

  constructor() {
    super();
    this.STORAGE_KEY = 'international-popup-shown';
    if (!window.SHP?.baseUrl) {
      throw new Error('Base URL not configured');
    }
    this.FETCH_URL = window.SHP.baseUrl + '/country';
    this.ALLOWED_COUNTRIES = ['us'];
    this.templateLoaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.checkAndShowInternationalPopup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removePopupEventListeners();
  }

  async checkAndShowInternationalPopup() {
    if (this.hasPopupBeenShown()) {
      return;
    }

    try {
      const isInternational = await this.checkIfInternational();

      if (isInternational) {
        await this.loadAndInjectTemplate();
        // The rest of the logic is triggered by the dialog-component:ready event
      }
    } catch (error) {
      console.warn('Failed to check international status:', error);
    }
  }

  async loadAndInjectTemplate() {
    if (this.templateLoaded) {
      return;
    }

    // Load dialog.js if not already loaded
    await this.loadDialogScript();

    // Set up event listener before injecting template
    // This ensures we catch the event when dialog-component is ready
    this.addEventListener(
      'dialog-component:ready',
      async () => {
        // Wait for refs to be updated after template injection
        // The MutationObserver updates refs asynchronously, so we need to wait
        await this.waitForRefs();
        this.showInternationalPopup();
        this.setupPopupEventListeners();
      },
      { once: true }
    );

    // Get the template from the script tag
    const templateScript = document.getElementById('international-popup-template');
    if (!templateScript) {
      console.warn('International popup template not found');
      return;
    }

    // Inject the template HTML into this element
    const templateHTML = templateScript.textContent.trim();
    this.innerHTML = templateHTML;

    this.templateLoaded = true;
  }

  /**
   * Wait for refs to be updated by the MutationObserver
   * This ensures that refs are populated before accessing them
   */
  async waitForRefs() {
    // Wait for the next microtask to allow MutationObserver to process
    await Promise.resolve();

    // Check if dialogComponent ref is available, if not wait a bit more
    let attempts = 0;
    const maxAttempts = 10;
    while (!this.refs?.dialogComponent && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 10));
      attempts++;
    }
  }

  async loadDialogScript() {
    // Check if dialog.js is already loaded
    if (customElements.get('dialog-component')) {
      return Promise.resolve();
    }

    // Check if script is already in the document
    const existingScript = /** @type {HTMLScriptElement | null} */ (
      document.querySelector('script[src*="dialog.js"]')
    );
    if (existingScript) {
      // Wait for it to load and custom element to be defined
      return new Promise(
        (
          /** @type {(value?: unknown) => void} */ resolve,
          /** @type {(reason?: any) => void} */ reject
        ) => {
          if (customElements.get('dialog-component')) {
            resolve();
            return;
          }

          const checkCustomElement = () => {
            if (customElements.get('dialog-component')) {
              resolve();
            } else {
              // Keep checking until defined or timeout
              /** @type {number | null} */
              let checkInterval = null;
              const timeoutId = setTimeout(() => {
                if (checkInterval) clearInterval(checkInterval);
                if (customElements.get('dialog-component')) {
                  resolve();
                } else {
                  // Reject after 2 seconds to signal failure
                  reject(new Error('Dialog component not defined after timeout'));
                }
              }, 2000);

              checkInterval = setInterval(() => {
                if (customElements.get('dialog-component')) {
                  clearTimeout(timeoutId);
                  clearInterval(checkInterval);
                  resolve();
                }
              }, 100);
            }
          };

          // Check if script is already loaded
          if (existingScript.dataset.loaded === 'true' || customElements.get('dialog-component')) {
            checkCustomElement();
          } else {
            existingScript.addEventListener(
              'load',
              () => {
                existingScript.dataset.loaded = 'true';
                checkCustomElement();
              },
              { once: true }
            );
            // Also check immediately in case it's already loaded
            if (customElements.get('dialog-component')) {
              checkCustomElement();
            }
          }
        }
      );
    }

    // Dynamically load dialog.js
    return new Promise(
      (
        /** @type {(value?: unknown) => void} */ resolve,
        /** @type {(reason?: any) => void} */ reject
      ) => {
        const script = document.createElement('script');
        script.src = this.getDialogScriptUrl();
        script.type = 'module';
        script.onload = () => {
          // Wait a bit for custom element to be defined
          let timeoutId;
          const checkInterval = setInterval(() => {
            if (customElements.get('dialog-component')) {
              clearInterval(checkInterval);
              clearTimeout(timeoutId);
              resolve();
            }
          }, 10);
          // Timeout after 5 seconds
          timeoutId = setTimeout(() => {
            clearInterval(checkInterval);
            if (customElements.get('dialog-component')) {
              resolve();
            } else {
              reject(new Error('Dialog component failed to load'));
            }
          }, 5000);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      }
    );
  }

  getDialogScriptUrl() {
    // Get URL from data attribute
    const dialogScriptUrl = this.dataset.dialogScriptUrl;
    if (dialogScriptUrl) {
      return dialogScriptUrl;
    }

    // Fallback: try to find the dialog.js script URL from existing scripts
    const existingDialogScript = /** @type {HTMLScriptElement | null} */ (
      document.querySelector('script[src*="dialog.js"]')
    );
    if (existingDialogScript) {
      return existingDialogScript.src;
    }

    // Last resort: construct a relative URL (may not work in all cases)
    return '/assets/dialog.js';
  }

  async checkIfInternational() {
    try {
      const response = await fetch(this.FETCH_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const currentCountry = data.cfCountryFromCf.toLowerCase();
      const isInternational = !this.ALLOWED_COUNTRIES.includes(currentCountry);

      return isInternational;
    } catch (error) {
      console.error(`International check result: ${error}`);

      return false;
    }
  }

  showInternationalPopup() {
    // Try to get dialogComponent from refs first
    let dialogComponent = /** @type {HTMLElement & { showDialog?: () => void } | undefined} */ (
      this.refs?.dialogComponent
    );

    // Fallback: query directly if refs aren't available yet
    if (!dialogComponent) {
      dialogComponent = /** @type {HTMLElement & { showDialog?: () => void } | undefined} */ (
        this.querySelector('dialog-component[ref="dialogComponent"]') ||
          this.querySelector('dialog-component')
      );
    }

    if (!dialogComponent) {
      console.warn('International popup dialog component not found in refs');
      return;
    }

    if (typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    } else {
      console.warn('Dialog component showDialog method not available');
    }
  }

  setupPopupEventListeners() {
    // Try to get dialogComponent from refs first
    let dialogComponent = /** @type {HTMLElement & { closeDialog?: () => void } | undefined} */ (
      this.refs?.dialogComponent
    );

    // Fallback: query directly if refs aren't available yet
    if (!dialogComponent) {
      dialogComponent = /** @type {HTMLElement & { closeDialog?: () => void } | undefined} */ (
        this.querySelector('dialog-component[ref="dialogComponent"]') ||
          this.querySelector('dialog-component')
      );
    }

    if (!dialogComponent) return;

    const continueButton = dialogComponent.querySelector('[ref="continueButton"]');
    const stayButton = dialogComponent.querySelector('[ref="stayButton"]');
    const closeButton = dialogComponent.querySelector('[ref="closeButton"]');

    const handlePopupInteraction = () => {
      this.markPopupAsShown();
      this.removePopupEventListeners();
    };

    const handleCloseClick = (/** @type {Event} */ event) => {
      event.preventDefault();
      handlePopupInteraction();
      if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      }
    };

    dialogComponent.addEventListener('dialog:close', handlePopupInteraction);

    if (continueButton) {
      continueButton.addEventListener('click', handlePopupInteraction);
    }

    if (stayButton) {
      stayButton.addEventListener('click', handlePopupInteraction);
    }

    if (closeButton) {
      closeButton.addEventListener('click', handleCloseClick);
    }

    this.popupEventCleanup = () => {
      dialogComponent.removeEventListener('dialog:close', handlePopupInteraction);

      if (continueButton) {
        continueButton.removeEventListener('click', handlePopupInteraction);
      }

      if (stayButton) {
        stayButton.removeEventListener('click', handlePopupInteraction);
      }

      if (closeButton) {
        closeButton.removeEventListener('click', handleCloseClick);
      }
    };
  }

  removePopupEventListeners() {
    if (this.popupEventCleanup) {
      this.popupEventCleanup();
      this.popupEventCleanup = null;
    }
  }

  hasPopupBeenShown() {
    try {
      const shown = localStorage.getItem(this.STORAGE_KEY);

      return shown === 'true';
    } catch (error) {
      console.warn('Failed to check localStorage:', error);
      return false;
    }
  }

  markPopupAsShown() {
    try {
      localStorage.setItem(this.STORAGE_KEY, 'true');
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }
}

if (!customElements.get('international-dialog-handler')) {
  customElements.define('international-dialog-handler', InternationalDialogHandler);
}
